import api from './api';

export interface CampaignInitiateRequest {
  template_name: string;
  phone_number: string;
  customer_name: string;
  skip_identity_validation?: boolean;
  reset_conversation_memory?: boolean;
  header_media_type?: 'image' | 'video';
  header_media_id?: string;
  header_media_url?: string;
  header_image_media_id?: string;
  header_image_url?: string;
  schedule_reengagement?: boolean;
  reengagement_delay_business_days?: number;
  no_answer_reengagement_attempts?: number;
  no_answer_reengagement_increment_business_days?: number;
  project_interest?: string;
}

export interface CampaignInitiateResponse {
  ok: boolean;
  template_name: string;
  to: string;
  language_code: string;
  sent_at: string;
  message_id?: string | null;
  conversation_id?: string | null;
  stored_message_id?: string | null;
  memory_appended?: boolean;
  warnings?: string[];
  error?: string | null;
}

export interface CampaignWebhookCreateRequest {
  campaign_name: string;
  voice_model_selection?: string | null;
  template_name: string;
  skip_identity_validation?: boolean;
  runtime_variable_names?: string[];
  header_media_type?: 'image' | 'video';
  header_media_id?: string;
  header_media_url?: string;
  header_image_media_id?: string;
  header_image_url?: string;
  hubspot_signature_secret?: string;
  schedule_reengagement?: boolean;
  reengagement_delay_business_days?: number;
  no_answer_reengagement_attempts?: number;
  no_answer_reengagement_increment_business_days?: number;
  project_interest?: string;
}

export interface CampaignWebhookCreateResponse {
  campaign_id: string;
  campaign_name: string;
  voice_model_selection?: string | null;
  template_name: string;
  language_code: string;
  status: string;
  is_live: boolean;
  webhook_url: string;
  bearer_token: string;
  bearer_token_hint: string;
  runtime_variable_names: string[];
  created_at: string;
}

export interface CampaignWebhookConfigUpdateRequest {
  project_interest?: string | null;
  voice_model_selection?: string | null;
  skip_identity_validation?: boolean;
  schedule_reengagement?: boolean;
  reengagement_delay_business_days?: number | null;
  no_answer_reengagement_attempts?: number | null;
  no_answer_reengagement_increment_business_days?: number | null;
  header_media_type?: 'image' | 'video' | null;
  header_media_id?: string | null;
  header_media_url?: string | null;
  header_image_media_id?: string | null;
  header_image_url?: string | null;
}

export interface CampaignHeaderImageUploadResponse {
  media_id: string;
  image_url: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
}

export interface CampaignHeaderMediaUploadResponse {
  media_id: string;
  media_url: string;
  media_type: 'image' | 'video';
  filename: string;
  mime_type: string;
  size_bytes: number;
  image_url?: string | null;
}

export type CampaignBulkTemplateMode = 'named' | 'positional';

export interface CampaignBulkValidationRow {
  row_number: number;
  raw_number?: string | null;
  phone_number?: string | null;
  variables: Record<string, string>;
  is_valid: boolean;
  errors: string[];
}

export interface CampaignBulkValidateResponse {
  validation_token: string;
  template_name: string;
  language_code: string;
  mode: CampaignBulkTemplateMode;
  expected_variables: string[];
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  rows: CampaignBulkValidationRow[];
}

export interface CampaignBulkSendResponse {
  campaign_id: string;
  status: string;
  queued_at: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
}

export interface CampaignBulkStatusResponse {
  campaign_type?: 'bulk' | 'webhook';
  campaign_name?: string | null;
  campaign_id: string;
  template_name: string;
  language_code: string;
  source_filename?: string | null;
  project_interest?: string | null;
  voice_model_selection?: string | null;
  skip_identity_validation?: boolean;
  reset_conversation_memory?: boolean;
  schedule_reengagement?: boolean;
  reengagement_delay_business_days?: number | null;
  no_answer_reengagement_attempts?: number | null;
  no_answer_reengagement_increment_business_days?: number | null;
  header_media_type?: 'image' | 'video' | null;
  header_media_id?: string | null;
  header_media_url?: string | null;
  header_image_media_id?: string | null;
  header_image_url?: string | null;
  status: string;
  is_live?: boolean | null;
  stopped_at?: string | null;
  webhook_endpoint_url?: string | null;
  runtime_variable_names?: string[];
  total_triggers?: number;
  successful_triggers?: number;
  failed_triggers?: number;
  last_triggered_at?: string | null;
  error_message?: string | null;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  processed_rows: number;
  sent_rows: number;
  failed_rows: number;
  skipped_rows: number;
  pending_rows: number;
  processing_rows: number;
  delivered_rows: number;
  read_rows: number;
  retry_scheduled_rows: number;
  retry_exhausted_rows: number;
  unreachable_rows: number;
  opted_out_rows: number;
  progress_percent: number;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at: string;
}

export interface CampaignBulkListResponse {
  total_count: number;
  skip: number;
  limit: number;
  campaigns: CampaignBulkStatusResponse[];
}

export interface CampaignHistoryListResponse {
  total_count: number;
  skip: number;
  limit: number;
  campaigns: CampaignBulkStatusResponse[];
}

export interface CampaignBulkRecipient {
  id: number;
  row_number: number;
  phone_number: string;
  raw_number?: string | null;
  variables: Record<string, string>;
  warnings?: string[];
  status: string;
  error_message?: string | null;
  whatsapp_message_id?: string | null;
  wa_status?: string | null;
  wa_error_code?: number | null;
  wa_error_description?: string | null;
  retry_count?: number | null;
  max_retries?: number | null;
  next_retry_at?: string | null;
  retry_exhausted?: boolean | null;
  conversation_id?: string | null;
  delivery_status?: string | null;
  sent_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignBulkRecipientsResponse {
  campaign_id: string;
  total_count: number;
  skip: number;
  limit: number;
  recipients: CampaignBulkRecipient[];
}

export interface CampaignBulkRecipientRetryResponse {
  campaign_id: string;
  recipient_id: number;
  wa_status?: string | null;
  whatsapp_message_id?: string | null;
  retry_count?: number | null;
  next_retry_at?: string | null;
  retry_exhausted?: boolean | null;
  error_message?: string | null;
}

export interface CampaignWebhookEventRetryResponse {
  ok: boolean;
  campaign_id: string;
  request_id: string;
  conversation_id?: string | null;
  stored_message_id?: string | null;
  whatsapp_message_id?: string | null;
  status: string;
}

export interface CampaignWebhookEvent {
  id: number;
  request_id: string;
  trigger_number: number;
  phone_number?: string | null;
  customer_name?: string | null;
  hubspot_deal_id?: string | null;
  hubspot_contact_id?: string | null;
  email?: string | null;
  voice_model_selection?: string | null;
  variables: Record<string, string>;
  status: string;
  error_message?: string | null;
  conversation_id?: string | null;
  stored_message_id?: string | null;
  whatsapp_message_id?: string | null;
  wa_status?: string | null;
  wa_error_code?: number | null;
  wa_error_description?: string | null;
  created_at: string;
  processed_at?: string | null;
  updated_at: string;
}

export interface CampaignWebhookEventsResponse {
  campaign_id: string;
  total_count: number;
  skip: number;
  limit: number;
  events: CampaignWebhookEvent[];
}

export type CampaignCostReportStatus = 'queued' | 'running' | 'completed' | 'failed' | 'expired';

export interface CampaignCostReportJob {
  id: string;
  status: CampaignCostReportStatus;
  start_date?: string | null;
  end_date?: string | null;
  currency: string;
  billing_timezone: string;
  meta_cost: string | number;
  fallback_cost: string | number;
  unpriced_count: number;
  source_metadata_json?: Record<string, unknown> | null;
  warnings_json?: string[] | null;
  error_message?: string | null;
  created_by_email?: string | null;
  created_at: string;
  queued_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  expires_at?: string | null;
}

export interface CampaignCostReportListResponse {
  total_count: number;
  reports: CampaignCostReportJob[];
}

export const normalizePhoneNumber = (rawPhone: string): string => {
  return (rawPhone || '').replace(/\D+/g, '');
};

class CampaignService {
  async initiateCampaign(payload: CampaignInitiateRequest): Promise<CampaignInitiateResponse> {
    const response = await api.post<CampaignInitiateResponse>('/campaigns/initiate', payload);
    return response.data;
  }

  async uploadCampaignHeaderMedia(file: File): Promise<CampaignHeaderMediaUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<CampaignHeaderMediaUploadResponse>(
      '/campaigns/header-media/upload',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Large media uploads can exceed the default 10s axios timeout.
        timeout: 600000,
      }
    );
    return response.data;
  }

  async uploadCampaignHeaderImage(file: File): Promise<CampaignHeaderImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<CampaignHeaderImageUploadResponse>(
      '/campaigns/header-image/upload',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      }
    );
    return response.data;
  }

  async createWebhookCampaign(payload: CampaignWebhookCreateRequest): Promise<CampaignWebhookCreateResponse> {
    const response = await api.post<CampaignWebhookCreateResponse>('/campaigns/webhook', payload);
    return response.data;
  }

  async downloadBulkTemplate(): Promise<Blob> {
    const response = await api.get('/campaigns/bulk/template.xlsx', {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async validateBulkCampaign(formData: FormData): Promise<CampaignBulkValidateResponse> {
    const response = await api.post<CampaignBulkValidateResponse>(
      '/campaigns/bulk/validate',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  async sendBulkCampaign(validationToken: string): Promise<CampaignBulkSendResponse> {
    const response = await api.post<CampaignBulkSendResponse>('/campaigns/bulk/send', {
      validation_token: validationToken,
    });
    return response.data;
  }

  async getBulkCampaignStatus(campaignId: string): Promise<CampaignBulkStatusResponse> {
    const response = await api.get<CampaignBulkStatusResponse>(`/campaigns/bulk/${campaignId}`);
    return response.data;
  }

  async listBulkCampaigns(options: { status?: string; skip?: number; limit?: number } = {}): Promise<CampaignBulkListResponse> {
    const response = await api.get<CampaignBulkListResponse>('/campaigns/bulk', { params: options });
    return response.data;
  }

  async listCampaigns(options: { status?: string; skip?: number; limit?: number } = {}): Promise<CampaignHistoryListResponse> {
    const response = await api.get<CampaignHistoryListResponse>('/campaigns/history', { params: options });
    return response.data;
  }

  async createCostReport(options: { start_date?: string; end_date?: string } = {}): Promise<CampaignCostReportJob> {
    const response = await api.post<CampaignCostReportJob>('/campaigns/cost-reports', options);
    return response.data;
  }

  async listCostReports(options: { skip?: number; limit?: number } = {}): Promise<CampaignCostReportListResponse> {
    const response = await api.get<CampaignCostReportListResponse>('/campaigns/cost-reports', { params: options });
    return response.data;
  }

  async getCostReport(reportId: string): Promise<CampaignCostReportJob> {
    const response = await api.get<CampaignCostReportJob>(`/campaigns/cost-reports/${reportId}`);
    return response.data;
  }

  async downloadCostReport(reportId: string): Promise<Blob> {
    const response = await api.get(`/campaigns/cost-reports/${reportId}/download`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async getCampaignStatus(campaignId: string): Promise<CampaignBulkStatusResponse> {
    const response = await api.get<CampaignBulkStatusResponse>(`/campaigns/history/${campaignId}`);
    return response.data;
  }

  async updateWebhookCampaignConfig(
    campaignId: string,
    payload: CampaignWebhookConfigUpdateRequest
  ): Promise<CampaignBulkStatusResponse> {
    const response = await api.patch<CampaignBulkStatusResponse>(
      `/campaigns/history/${campaignId}/webhook-config`,
      payload
    );
    return response.data;
  }

  async stopCampaign(campaignId: string): Promise<CampaignBulkStatusResponse> {
    const response = await api.post<CampaignBulkStatusResponse>(`/campaigns/history/${campaignId}/stop`);
    return response.data;
  }

  async getWebhookCampaignEvents(
    campaignId: string,
    options: { skip?: number; limit?: number; status?: string } = {}
  ): Promise<CampaignWebhookEventsResponse> {
    const response = await api.get<CampaignWebhookEventsResponse>(
      `/campaigns/history/${campaignId}/events`,
      { params: options }
    );
    return response.data;
  }

  async getBulkCampaignRecipients(
    campaignId: string,
    options: { skip?: number; limit?: number; status?: string } = {}
  ): Promise<CampaignBulkRecipientsResponse> {
    const response = await api.get<CampaignBulkRecipientsResponse>(
      `/campaigns/bulk/${campaignId}/recipients`,
      { params: options }
    );
    return response.data;
  }

  async retryBulkCampaignRecipient(
    campaignId: string,
    recipientId: number
  ): Promise<CampaignBulkRecipientRetryResponse> {
    const response = await api.post<CampaignBulkRecipientRetryResponse>(
      `/campaigns/bulk/${campaignId}/recipients/${recipientId}/retry`
    );
    return response.data;
  }

  async retryWebhookCampaignEvent(
    campaignId: string,
    eventId: number
  ): Promise<CampaignWebhookEventRetryResponse> {
    const response = await api.post<CampaignWebhookEventRetryResponse>(
      `/campaigns/history/${campaignId}/events/${eventId}/retry`
    );
    return response.data;
  }

  async exportBulkCampaignRecipients(
    campaignId: string,
    options: { status?: string } = {}
  ): Promise<Blob> {
    const response = await api.get(`/campaigns/bulk/${campaignId}/recipients/export.xlsx`, {
      params: options,
      responseType: 'blob',
    });
    return response.data as Blob;
  }
}

export const campaignService = new CampaignService();
export default campaignService;
